/**
Copyright (c) 2023, 2024, 2025 Amica Corp.
All rights reserved.

This source code, resulting binary forms, and associated documentation
(the "Software") may only be used, modified, or distributed, with or
without modification, under the terms of a written license agreement
with Amica Corp. (the "License"). All rights to manufacture, use,
redistribute, or sell anything described herein, in whole or in part,
are granted only as explicitly permitted by such License. The receipt
or possession of the Software does not convey any of these rights.

This Software is provided "AS IS," WITHOUT WARRANTY OF ANY KIND, express
or implied, including but not limited to the implied warranties of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.

For additional terms, see https://amica.cloud/software-copyright/
 */

/* eslint-disable react/no-this-in-sfc */
import { useState, useRef, useEffect, useMemo } from 'react';
import { endOfDay, isSameDay, startOfDay } from 'date-fns';
import { Badge, InputAdornment, Tooltip, Box } from '@mui/material';
import { ClickAwayListener } from '@mui/base';
import { STAY_DATE_END_DATE, STAY_DATE_START_DATE } from 'modules/common/constants/date-range';
import { DateRange, defaultStaticRanges, DefinedRange } from 'react-date-range';
import styles from 'modules/dashboard/components/tab-container/inner-filter/inner-filter.module.scss';
import 'react-date-range/dist/styles.css';
import 'react-date-range/dist/theme/default.css';
import { useSelector } from 'react-redux';
import { selectStayDatesPresetList } from 'modules/users/selectors';
import { selectPaceBookingDate } from 'modules/dashboard/components/tab-container/pace-tabs/selectors';
import { selectWorkbookBookingDate } from 'modules/dashboard/components/tab-container/workbook-report/selectors';
import { selectSummaryBookingDate } from 'modules/dashboard/components/tab-container/summary-tab/selectors';
import { selectRegionMapBookingDate } from 'modules/dashboard/components/tab-container/regional-map-tab/selectors';
import { canEnabledCurrentDatePreSet } from 'modules/dashboard/functions';
import { TextField } from './style';
import customPresets, {
  getStayDateCurrentPreset,
  getStayDatePreset,
} from '../../constants/custom-presets';
import variants from '../../theme/variants';
import {
  selectAppTheme,
  selectLastFullBookingDate,
  selectMaxStayDate,
  selectTabList,
  selectTabValue,
  selectTrendBookingDate,
} from '../../../dashboard/selectors';

/**
 * Custom stay date range picker common component that uses across modules for select stay date range
 * @param {Function} handleChange - function for setting data range
 * @param {Array} ranges - start date and end data of the picker
 * @param {String} label - textfield label
 * @param {String} adornment - whether we need to adornment is beginning or not
 * @param {String} dateValue - selected date value
 * @param {String} cssClassName - use style for entire component
 * @returns
 */
const CustomStayDateRangePicker = (props) => {
  // destruct the props object
  const {
    handleChange,
    ranges,
    label,
    adornment,
    dateValue,
    cssClassName,
    id = '',
    isLeap,
  } = props;
  //
  const presetList = useSelector(selectStayDatesPresetList);
  const themeName = useSelector(selectAppTheme);
  const selectedVariant = variants.find((variant) => variant.name === themeName);
  const lastFullBookingDate = useSelector(selectLastFullBookingDate);
  const trendBookingDate = useSelector(selectTrendBookingDate);
  const paceBookingDate = useSelector(selectPaceBookingDate);
  const workbookBookingDate = useSelector(selectWorkbookBookingDate);
  const summaryBookingDate = useSelector(selectSummaryBookingDate);
  const regionalMapBookingDate = useSelector(selectRegionMapBookingDate);
  const maxStayDate = useSelector(selectMaxStayDate);
  const tab = useSelector(selectTabValue);
  const TAB_VALUE = useSelector(selectTabList);
  //
  const [open, setOpen] = useState(false);
  const [formattedPresetList, setFormattedPresetList] = useState([]);
  //
  const refTarget = useRef(null);
  //
  const customStayPresets = useMemo(
    () =>
      lastFullBookingDate && maxStayDate ? getStayDatePreset(lastFullBookingDate, maxStayDate) : [],
    [lastFullBookingDate, maxStayDate]
  );
  const customStayCurrentDatePresets = useMemo(
    () => getStayDateCurrentPreset(lastFullBookingDate),
    [lastFullBookingDate]
  );
  // Check the current booking date is eligible for custom current date presets or not based on the selected tab
  const isCustomCurrentStayPresets = useMemo(() => {
    let bookingDate;
    //
    switch (tab) {
      case TAB_VALUE.TREND_TIME:
        bookingDate = trendBookingDate;
        break;
      case TAB_VALUE.PACE:
      case TAB_VALUE.PACE_TEST:
        bookingDate = paceBookingDate;
        break;
      case TAB_VALUE.WORKBOOK_REPORT:
        bookingDate = workbookBookingDate;
        break;
      case TAB_VALUE.SUMMARY:
        bookingDate = summaryBookingDate;
        break;
      case TAB_VALUE.REGION_MAP:
        bookingDate = regionalMapBookingDate;
        break;
      default:
        bookingDate = null;
    }
    //
    return bookingDate ? canEnabledCurrentDatePreSet(lastFullBookingDate, bookingDate) : false;
  }, [
    trendBookingDate,
    paceBookingDate,
    workbookBookingDate,
    summaryBookingDate,
    regionalMapBookingDate,
    tab,
  ]);
  //
  useEffect(() => {
    if (presetList?.length > 0) {
      const formattedSet = presetList?.map((set) => ({
        label: set?.presetName,
        range: () => ({
          startDate: startOfDay(new Date(set?.startDate)),
          endDate: endOfDay(new Date(set?.endDate)),
        }),
        isSelected(range) {
          const definedRange = this.range();
          return (
            isSameDay(range.startDate, definedRange.startDate) &&
            isSameDay(range.endDate, definedRange.endDate)
          );
        },
      }));
      setFormattedPresetList(formattedSet);
    }
  }, [presetList]);
  // checks the range is exited or not and set date range
  const dateRange =
    ranges !== null
      ? [
          {
            startDate: new Date(ranges[0].startDate),
            endDate: new Date(ranges[0].endDate),
            key: 'selection',
            color: selectedVariant?.palette.primary.main,
          },
        ]
      : [
          {
            startDate: new Date(),
            endDate: new Date(),
            key: 'selection',
            color: selectedVariant?.palette.primary.main,
          },
        ];
  //
  return (
    <ClickAwayListener onClickAway={() => setOpen(false)}>
      <div className="calendarWrap">
        <Box position="relative" display="inline-block" width="100%" className="stay-date-range">
          <TextField
            id="stay date range picker"
            InputProps={{
              startAdornment: <InputAdornment position="start">{adornment}</InputAdornment>,
            }}
            label={label}
            value={dateValue}
            size="small"
            color="primary"
            onClick={() => setOpen(!open)}
            fullWidth
          />
          {isLeap && (
            <Tooltip
              title="Your selection range includes a leap day. Totals displayed include the leap day values where applicable."
              arrow
            >
              <Badge
                color="error"
                variant="dot"
                invisible={!isLeap}
                anchorOrigin={{
                  vertical: 'bottom',
                  horizontal: 'right',
                }}
                sx={{
                  position: 'absolute',
                  top: 8,
                  right: 8,
                  cursor: 'pointer',
                }}
              />
            </Tooltip>
          )}
        </Box>
        <div ref={refTarget}>
          {open && (
            <>
              <DefinedRange
                onChange={handleChange}
                ranges={dateRange}
                className={styles.calendarElement3}
                inputRanges={[]}
                staticRanges={[
                  defaultStaticRanges[0],
                  customPresets[0],
                  defaultStaticRanges[2],
                  defaultStaticRanges[4],
                  customPresets[3],
                  customPresets[1],
                  customPresets[2],
                  ...(isCustomCurrentStayPresets ? customStayCurrentDatePresets : []),
                  ...customStayPresets,
                  ...formattedPresetList,
                ]}
                renderStaticRangeLabel={(staticRange) =>
                  staticRange?.toolTip ? (
                    <Tooltip title={staticRange.toolTip} arrow>
                      <span>{staticRange.label}</span>
                    </Tooltip>
                  ) : (
                    <span>{staticRange.label}</span>
                  )
                }
              />
              <DateRange
                id={id}
                onChange={handleChange}
                editableDateInputs
                moveRangeOnFirstSelection={false}
                ranges={dateRange}
                months={1}
                fixedHeight
                direction="horizontal"
                className={cssClassName}
                maxDate={new Date(STAY_DATE_END_DATE)}
                minDate={new Date(STAY_DATE_START_DATE)}
                dateDisplayFormat="yyyy-MM-dd"
              />
            </>
          )}
        </div>
      </div>
    </ClickAwayListener>
  );
};
//
export default CustomStayDateRangePicker;
